import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, Target, CalendarDays, GanttChartSquare, FolderKanban,
  Briefcase, FileText, Receipt, Clock, Handshake, Users, TrendingUp,
  UserCog, Calculator, Building2, ScrollText, Upload, BarChart3,
  CreditCard, MapPin, Menu, X, Landmark, Inbox, ChevronDown,
  CalendarClock, Palmtree, Route, FileClock, FileInput, Tags,
  CalendarRange, PenTool, LineChart, PieChart, Gauge, Bell, CalendarSync, Ruler,
} from 'lucide-react'
import useAuthStore from '../store/authStore'
import {
  isAdminUser, canSeeCompanyDashboard, roleLabel, canViewInvoices, canManageEstimates,
  isPartnerUser, isPmUser,
} from '../services/users'
import useFeaturesStore from '../store/featuresStore'
import { pmRequestsService } from '../services/pmRequests'
import { timesheetsService, HOURS_CHANGED_EVENT } from '../services/timesheets'

/**
 * Menu per gruppi: con oltre venti voci una lista piatta diventa uno
 * scroll cieco. Ogni gruppo ha un colore (accento su icona e voce attiva)
 * e si apre/chiude; il gruppo della pagina corrente resta sempre aperto e
 * le preferenze si ricordano tra le sessioni.
 *
 * need: 'all' (chiunque) | 'company' (admin/manager/soci) | 'admin'
 */
export const NAV_GROUPS = [
  {
    id: 'panoramica',
    label: 'Panoramica',
    color: 'blue',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, need: 'all' },
      { to: '/pm', label: 'Le mie commesse', icon: Target, need: 'company' },
      { to: '/calendar', label: 'Calendario', icon: CalendarDays, need: 'all' },
      { to: '/agenda', label: 'La mia agenda', icon: CalendarClock, need: 'all' },
      // vicino al calendario, non in Amministrazione: prenotare una sala è
      // un'azione operativa legata agli appuntamenti, non una configurazione
      { to: '/sedi', label: 'Sedi e sale', icon: MapPin, need: 'company' },
    ],
  },
  {
    id: 'lavoro',
    label: 'Lavoro',
    color: 'emerald',
    items: [
      { to: '/projects', label: 'Commesse', icon: FolderKanban, need: 'all' },
      { to: '/richieste', label: 'Richieste al PM', icon: Inbox, need: 'all' },
      { to: '/timesheets', label: 'Ore', icon: Clock, need: 'all' },
      { to: '/leaves', label: 'Ferie e permessi', icon: Palmtree, need: 'all' },
    ],
  },
  {
    id: 'report',
    label: 'Report',
    color: 'indigo',
    items: [
      { to: '/cruscotto', label: 'Cruscotto direzionale', icon: BarChart3, need: 'company' },
      { to: '/timeline', label: 'Timeline', icon: GanttChartSquare, need: 'company' },
      { to: '/lavorazioni-anno', label: 'Lavorazioni per anno', icon: CalendarRange, need: 'company' },
      { to: '/carico-team', label: 'Carico del team', icon: Gauge, need: 'company' },
      // nome allineato al titolo della pagina ("Statistiche team / ingaggi"):
      // non è un doppione di "Carico del team" (pianificazione interna), sono
      // gli ingaggi di imprese/professionisti ESTERNI — nomi troppo simili
      // rischiavano di far aprire quella sbagliata
      { to: '/admin/team-stats', label: 'Statistiche collaboratori', icon: PieChart, need: 'admin' },
    ],
  },
  {
    id: 'commerciale',
    label: 'Commerciale',
    color: 'violet',
    items: [
      { to: '/opportunities', label: 'Opportunità', icon: Briefcase, need: 'estimates' },
      { to: '/estimates', label: 'Preventivi', icon: FileText, need: 'estimates' },
      // listino e coefficienti: stesso pubblico (chi fa preventivi) e stesso
      // scopo (prezzare) di Preventivi/Opportunità — prima erano divisi fra
      // "Report" e "Amministrazione", lontani da chi li usa davvero
      { to: '/admin/line-templates', label: 'Listino voci', icon: Tags, need: 'estimates' },
      { to: '/coefficienti', label: 'Coefficienti attività', icon: Ruler, need: 'estimates' },
      { to: '/clients', label: 'Clienti', icon: Users, need: 'all' },
      { to: '/collaborators', label: 'Collaboratori', icon: Handshake, need: 'company' },
    ],
  },
  {
    id: 'denaro',
    label: 'Fatture e denaro',
    color: 'amber',
    items: [
      { to: '/invoices?status=draft', label: 'Avvisi di parcella', icon: FileClock, need: 'invoices' },
      { to: '/invoices', label: 'Fatture', icon: Receipt, need: 'invoices' },
      { to: '/invoices/received', label: 'Fatture ricevute', icon: FileInput, need: 'admin' },
      { to: '/invoices/reconciliation', label: 'Riconciliazione banca', icon: Landmark, need: 'admin' },
      { to: '/invoices/cashflow', label: 'Cashflow', icon: TrendingUp, need: 'admin' },
      { to: '/compensi-firma', label: 'Compensi per la firma', icon: PenTool, need: 'partner' },
      // rimborso spese ai collaboratori: un flusso di denaro, non un report
      // direzionale — stava in "Report" lontano dalle altre voci di cassa
      { to: '/rimborsi-km', label: 'Rimborsi km', icon: Route, need: 'company' },
      // termini di pagamento usati su preventivi e fatture: più vicino ai
      // soldi che alla configurazione di sistema
      { to: '/admin/payment-terms', label: 'Modalità di pagamento', icon: CreditCard, need: 'company' },
    ],
  },
  {
    id: 'impostazioni',
    label: 'Amministrazione',
    color: 'slate',
    items: [
      { to: '/admin/users', label: 'Gestione utenti', icon: UserCog, need: 'admin' },
      { to: '/admin/tax', label: 'Impostazioni fiscali', icon: Calculator, need: 'admin' },
      { to: '/admin/studio', label: 'Impostazioni studio', icon: Building2, need: 'admin' },
      { to: '/admin/calendario-google', label: 'Calendario Google', icon: CalendarSync, need: 'admin' },
      { to: '/admin/notifiche', label: 'Notifiche esterne', icon: Bell, need: 'admin' },
      { to: '/admin/audit-log', label: 'Registro azioni', icon: ScrollText, need: 'admin' },
      { to: '/admin/data-import', label: 'Importazione dati', icon: Upload, need: 'admin' },
    ],
  },
]

// Tailwind non accetta classi costruite a runtime: mappa esplicita.
const GROUP_STYLES = {
  blue: { dot: 'bg-blue-500', icon: 'text-blue-600', active: 'bg-blue-50 text-blue-700' },
  emerald: { dot: 'bg-emerald-500', icon: 'text-emerald-600', active: 'bg-emerald-50 text-emerald-700' },
  indigo: { dot: 'bg-indigo-500', icon: 'text-indigo-600', active: 'bg-indigo-50 text-indigo-700' },
  violet: { dot: 'bg-violet-500', icon: 'text-violet-600', active: 'bg-violet-50 text-violet-700' },
  amber: { dot: 'bg-amber-500', icon: 'text-amber-600', active: 'bg-amber-50 text-amber-800' },
  slate: { dot: 'bg-slate-400', icon: 'text-slate-500', active: 'bg-slate-100 text-slate-800' },
}

function canSee(item, user, controllingEnabled) {
  switch (item.need) {
    case 'all':
      return true
    case 'company':
      return canSeeCompanyDashboard(user)
    case 'admin':
      return isAdminUser(user)
    case 'partner':
      return canSeeCompanyDashboard(user) && !!user?.is_partner
    case 'invoices':
      return canViewInvoices(user)
    case 'estimates':
      return canManageEstimates(user)
    case 'controlling':
      // stessa regola di RoleGuard (['partner', 'pm']): niente is_staff senza ruolo
      return controllingEnabled && (isPartnerUser(user) || isPmUser(user))
    default:
      return false
  }
}

const OPEN_GROUPS_KEY = 'nav_open_groups'

export default function Layout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [openRequests, setOpenRequests] = useState(0)

  useEffect(() => {
    pmRequestsService
      .counters()
      .then((c) => setOpenRequests(c.assigned_to_me || 0))
      .catch(() => {})
  }, [])

  // Ore inserite dai colleghi in attesa di approvazione: badge sulla voce "Ore"
  // per chi le deve approvare. Si aggiorna ogni minuto, cambiando pagina e
  // subito dopo un'approvazione.
  const [hoursToApprove, setHoursToApprove] = useState(0)
  const canApproveHours = canSeeCompanyDashboard(user)
  useEffect(() => {
    if (!canApproveHours) return undefined
    const refresh = () =>
      timesheetsService
        .pendingCount()
        .then((c) => setHoursToApprove(c.to_approve || 0))
        .catch(() => {})
    refresh()
    const timer = setInterval(refresh, 60000)
    window.addEventListener(HOURS_CHANGED_EVENT, refresh)
    return () => {
      clearInterval(timer)
      window.removeEventListener(HOURS_CHANGED_EVENT, refresh)
    }
  }, [canApproveHours, location.pathname])
  const controllingEnabled = useFeaturesStore((s) => s.controllingEnabled)
  const resetFeatures = useFeaturesStore((s) => s.reset)

  // gruppi con almeno una voce visibile per questo utente
  const groups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((i) => canSee(i, user, controllingEnabled)),
  })).filter((g) => g.items.length > 0)
  // Il controllo di gestione e' una sezione a se', ma un gruppo di menu con
  // dentro una sola voce dallo stesso nome e' un livello sprecato: sta con
  // le altre voci che parlano di soldi.
  const voceControllo = {
    to: '/controlling',
    label: 'Controllo di gestione',
    icon: LineChart,
    need: 'controlling',
  }
  if (canSee(voceControllo, user, controllingEnabled)) {
    const denaro = groups.find((g) => g.id === 'denaro')
    if (denaro) denaro.items.push(voceControllo)
    else groups.push({ id: 'denaro', label: 'Fatture e denaro', color: 'amber', items: [voceControllo] })
  }

  const groupOf = (path) =>
    groups.find((g) => g.items.some((i) => path.startsWith(i.to.split('?')[0])))?.id

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || 'null')
      if (Array.isArray(saved)) return saved
    } catch {
      // preferenza illeggibile: si riparte dai default
    }
    // default: tutto aperto tranne l'amministrazione (la si apre di rado)
    return NAV_GROUPS.filter((g) => g.id !== 'impostazioni').map((g) => g.id)
  })

  useEffect(() => {
    localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups))
  }, [openGroups])

  const activeGroup = groupOf(location.pathname)

  // navigando in una pagina, il suo gruppo si apre da solo — ma resta
  // richiudibile: forzarlo aperto rendeva impossibile chiudere il gruppo
  // della sezione in cui si sta lavorando
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) =>
        prev.includes(activeGroup) ? prev : [...prev, activeGroup],
      )
    }
  }, [activeGroup])

  const isGroupOpen = (id) => openGroups.includes(id)
  const toggleGroup = (id) =>
    setOpenGroups((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )

  const handleLogout = () => {
    logout()
    resetFeatures() // così la prossima sessione rilegge il flag dal backend
    navigate('/login')
  }

  const close = () => setDrawerOpen(false)

  // Auto-close drawer on route change (mobile only — body has no effect on desktop)
  if (drawerOpen) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // (closing handled by NavLink onClick below)
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      <button
        type="button"
        className="md:hidden fixed top-3 left-3 z-30 bg-white border border-gray-200 shadow rounded-md p-2 text-gray-700"
        onClick={() => setDrawerOpen((v) => !v)}
        aria-label="Apri menu"
      >
        {drawerOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
      </button>

      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-10"
          onClick={close}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-20 w-64 bg-white border-r border-gray-200 flex flex-col transform transition-transform md:translate-x-0 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-gray-200">
          <h1 className="text-lg font-bold text-gray-800">Venetiae</h1>
          <p className="text-xs text-gray-500">Gestionale</p>
        </div>

        <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
          {groups.map((group) => {
            const style = GROUP_STYLES[group.color] || GROUP_STYLES.blue
            const expanded = isGroupOpen(group.id)
            return (
              <div key={group.id}>
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={expanded}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs uppercase tracking-wide text-gray-400 hover:text-gray-600"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} aria-hidden="true" />
                  <span className="font-medium">{group.label}</span>
                  <ChevronDown
                    size={14}
                    aria-hidden="true"
                    className={`ml-auto transition-transform ${expanded ? '' : '-rotate-90'}`}
                  />
                </button>

                {expanded && (
                  <div className="space-y-0.5 mt-0.5">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={close}
                        end={item.to === '/invoices'}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-4 pr-3 py-2 rounded-md text-sm transition-colors ${
                            isActive
                              ? `${style.active} font-medium`
                              : 'text-gray-700 hover:bg-gray-100'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <item.icon
                              size={18}
                              aria-hidden="true"
                              className={`shrink-0 ${isActive ? '' : style.icon}`}
                            />
                            <span className="truncate">{item.label}</span>
                            {item.to === '/richieste' && openRequests > 0 && (
                              <span
                                className="ml-auto bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5"
                                aria-label={`${openRequests} richieste da evadere`}
                              >
                                {openRequests}
                              </span>
                            )}
                            {item.to === '/timesheets' && hoursToApprove > 0 && (
                              <span
                                className="ml-auto bg-red-600 text-white text-xs font-bold rounded-full px-1.5 py-0.5"
                                aria-label={`${hoursToApprove} ore da approvare`}
                              >
                                {hoursToApprove}
                              </span>
                            )}
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Link
            to="/profile"
            onClick={close}
            className={`block text-sm hover:underline ${
              location.pathname === '/profile' ? 'text-blue-700' : 'text-gray-700'
            }`}
          >
            <strong>{user?.first_name || user?.username || 'Utente'}</strong>
            {user?.role && (
              <span className="text-xs text-gray-500 block">{roleLabel(user.role)}</span>
            )}
          </Link>
          <div className="text-xs text-gray-500 mb-3 truncate">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="w-full text-left text-sm text-red-600 hover:bg-red-50 px-3 py-2 rounded-md"
          >
            Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto md:ml-0 ml-0">
        <Outlet />
      </main>
    </div>
  )
}
